import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from 'src/app/Services/auth.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { NotificationTypes } from 'src/app/app.enums';
import { TranslocoService } from '@jsverse/transloco';

/**
 * EventArtistGuard
 * Prevents event-only artists from accessing full artist features
 * If user is event-only artist AND trying to access non-event route → block
 * If user is full artist → allow access to all routes
 */
@Injectable({
  providedIn: 'root'
})
export class EventArtistGuard implements CanActivate {

  // Routes that event-only artists ARE allowed to access
  private allowedRoutesForEventArtists: string[] = [
    '/events/my-participated-events',
    '/events/terms-accept',
    '/events/submit-artworks',
    '/events/submitted-artworks',     // Payment success page
    '/events/public',
    '/events/available',
    '/events/register',
    '/artists/artworks',              // Allow access to artworks page
    '/artists/connected-galleries',   // Allow access to connected galleries
    '/inbox',                          // Allow access to messages
    '/edit-profile',
    '/dashboard',
    '/logout'
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private notificationsService: NotificationsService,
    private transloco: TranslocoService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      return true; // Let AuthGuard handle this
    }

    // Check if user is event-only artist
    const isEventArtist = this.authService.isEventArtist();

    // If not event-only artist, allow access
    if (!isEventArtist) {
      return true;
    }

    // User is event-only artist - check if route is allowed
    const currentUrl = state.url;
    const isAllowedRoute = this.isRouteAllowed(currentUrl);

    if (isAllowedRoute) {
      return true;
    }

    // Route not allowed for event-only artists
    this.notificationsService.showNotification(
      this.transloco.translate('eventArtistGuard.upgradeRequiredTitle'),
      this.transloco.translate('eventArtistGuard.upgradeRequiredMessage'),
      NotificationTypes.Alert
    );

    // Redirect to participant dashboard
    return this.router.createUrlTree(['/events/my-participated-events']);
  }

  /**
   * Check if route is allowed for event-only artists
   */
  private isRouteAllowed(url: string): boolean {
    return this.allowedRoutesForEventArtists.some(route =>
      url.startsWith(route) || url === route
    );
  }
}
